/**
 * Final amounts in the payable unit of their currency.
 *
 * The MCF certifies amounts to the centime (line and group HT, VAT, specific
 * tax, PSVB, invoice total). Money that changes hands is counted in the
 * minor unit of the invoice currency (money.ts): for the franc CFA the whole
 * franc, for a two-decimal currency the cent. The difference between a
 * certified balance and what is paid in that unit is the settlement rounding
 * (« Arrondi »), persisted as InvoicePayment rows of method ARRONDI.
 *
 * Rule: the nearest unit, an exact half away from zero, the rounding SFE
 * spec §6.2 prescribes (to the nearest value) and the DGI MCF applies to an
 * exact half (measured: 2.205 → 2.21). Every surface reads the direction
 * from the single `PAYABLE_ROUNDING_DIRECTION` constant below.
 */
import { isArrondiPaymentMethod, isReceivedPaymentMethod } from "./settlement.js";
import { roundMoney, centimesPerMinorUnit, centimesToAmount, toCentimes, currencyCode, } from "./money.js";
/** The direction applied to every final amount (SFE §6.2, MCF measurement). */
export const PAYABLE_ROUNDING_DIRECTION = "nearest";
/** Label of the line that shows the rounding difference on a document. */
export const PAYABLE_ROUNDING_LABEL = "Arrondi";
/**
 * An amount brought to the payable unit of its currency.
 *
 * Works on the magnitude: a negative credit-note amount gives the same count
 * of units as its positive counterpart (−11.50 → −12 francs CFA).
 */
export function toPayableAmount(amount, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    const unit = centimesPerMinorUnit(currency);
    const centimes = toCentimes(amount);
    const magnitude = Math.abs(centimes);
    const units = direction === "down"
        ? Math.floor(magnitude / unit)
        : direction === "up"
            ? Math.ceil(magnitude / unit)
            : Math.floor((magnitude + unit / 2) / unit);
    if (units === 0)
        return 0;
    return centimesToAmount((centimes < 0 ? -units : units) * unit);
}
/** An amount in the payable unit of its currency, as `Money`. */
export function toPayableMoney(amount, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    return {
        currency: currencyCode(currency),
        minor: (toCentimes(toPayableAmount(amount, currency, direction)) /
            centimesPerMinorUnit(currency)),
    };
}
/** Splits an exact amount into its payable amount and the rounding line. */
export function payableAmount(amount, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    const exact = roundMoney(amount);
    const payable = toPayableAmount(exact, currency, direction);
    const arrondi = roundMoney(payable - exact);
    return { exact, payable, arrondi: arrondi === 0 ? 0 : arrondi };
}
/**
 * The exact net to pay of an invoice: total TTC plus the PSVB the seller
 * collects from the client. A credit note's stored totals are negative, and
 * so is its net.
 */
export function invoiceNetExact(totals) {
    return roundMoney(totals.totalTTC + (totals.totalPSVB ?? 0));
}
/** The net to pay of an invoice, split into payable amount and rounding. */
export function invoiceNetPayable(totals, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    return payableAmount(invoiceNetExact(totals), currency, direction);
}
/**
 * What settles an exact amount due: its payable amount, or the exact amount
 * when that is lower.
 */
function settlingCentimes(exactDue, currency, direction) {
    return Math.min(toCentimes(toPayableAmount(exactDue, currency, direction)), toCentimes(exactDue));
}
/** True once what was paid settles an exact amount due. Nothing is owed on `exactDue` ≤ 0. */
export function isPayableSettled(paid, exactDue, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    if (exactDue <= 0)
        return true;
    return toCentimes(paid) >= settlingCentimes(exactDue, currency, direction);
}
/**
 * The most that may be paid against an exact amount due: its payable
 * amount, or the exact amount when that is higher.
 */
export function payableCeiling(exactDue, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    if (exactDue <= 0)
        return 0;
    return Math.max(toPayableAmount(exactDue, currency, direction), roundMoney(exactDue));
}
/**
 * Balance still to pay in the payable unit: the payable amount of the exact
 * due minus what was paid; zero once the payment settles it.
 */
export function payableRemaining(exactDue, paid, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    if (isPayableSettled(paid, exactDue, currency, direction))
        return 0;
    return roundMoney(toPayableAmount(exactDue, currency, direction) - paid);
}
/**
 * Settlement amounts as declared to the MCF.
 *
 * SFE §2.22 has the payments the MCF records sum to the invoice total, to the
 * centime. The settlements' sum is matched against each of `exactTotals` in
 * order, first its exact amount, then its payable amount: on an exact match
 * the set is returned unchanged; on a payable match the last one carries the
 * rounding difference so the declaration sums to that exact total. A set
 * matching none is returned unchanged. With `exactTotals` [net, TTC], a sum
 * equal to the payable net is read as the net even when it also equals the
 * TTC (200 paid on a TTC of 200 and a net of 200,40 declares 200,40).
 */
export function declaredSettlementAmounts(settlements, exactTotals, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    const result = settlements.map((settlement) => ({ ...settlement }));
    if (result.length === 0)
        return result;
    const totals = typeof exactTotals === "number" ? [exactTotals] : exactTotals;
    const paidCentimes = result.reduce((sum, s) => sum + toCentimes(s.amount), 0);
    for (const total of totals) {
        if (toCentimes(total) === paidCentimes)
            return result;
        const { payable, arrondi } = payableAmount(total, currency, direction);
        if (arrondi === 0 || paidCentimes !== toCentimes(payable))
            continue;
        const last = result[result.length - 1];
        const declared = roundMoney(last.amount - arrondi);
        if (declared === 0 || Math.sign(declared) !== Math.sign(last.amount))
            return result;
        result[result.length - 1] = { ...last, amount: declared };
        return result;
    }
    return result;
}
/**
 * The settlements recorded at signing, in recording order, as declared to
 * the MCF (the rounding row is never declared), with the money they stand
 * for.
 */
export function settlementDeclaration(atSigning, exactTotals, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    const declarable = atSigning.filter((settlement) => !isArrondiPaymentMethod(settlement.method));
    const declared = declaredSettlementAmounts(declarable, exactTotals, currency, direction);
    const amountReceived = roundMoney(declarable
        .filter((settlement) => isReceivedPaymentMethod(settlement.method))
        .reduce((sum, settlement) => sum + settlement.amount, 0));
    return { declared, amountReceived };
}
/**
 * The amount received printed apart (« Montant reçu »): what the client
 * handed over at signing (`declaration.amountReceived`) when it differs
 * from the money received that `mcfDeclared`, the list the MCF recorded and
 * the document prints, holds for those settlements; null when they agree.
 * That list holds one entry per settlement of the declaration, in recording
 * order, then at most one remainder no settlement stands for: only its
 * first `declaration.declared.length` entries are read.
 */
export function amountReceivedApart(declaration, mcfDeclared) {
    const declaredReceived = mcfDeclared
        .slice(0, declaration.declared.length)
        .filter((settlement) => isReceivedPaymentMethod(settlement.method))
        .reduce((sum, settlement) => sum + toCentimes(settlement.amount), 0);
    return declaredReceived === toCentimes(declaration.amountReceived) ? null : declaration.amountReceived;
}
/**
 * Splits the settlements of an invoice into those declared to the MCF when
 * it was signed and those recorded afterwards.
 *
 * A settlement whose writer said where it was recorded
 * (`recordedAfterIssue`) is placed by that alone: false at signing, true
 * after. One that does not say is placed by the clock: `signedAt` is the
 * database clock of the signature (`recordedSignedAt`, or `signedAt` when
 * that is absent), and the settlement belongs to the signature when it was
 * recorded at or before it, or when there is no signature, since the MCF
 * will then receive it. The declaration is rebuilt as signing builds it, in
 * recording order (paidAt, then id); the rounding row belongs to neither
 * list.
 */
export function settlementsAtSigning(settlements, signedAt, exactTotals, currency, direction = PAYABLE_ROUNDING_DIRECTION) {
    const time = (value) => new Date(value).getTime();
    const ordered = [...settlements]
        .filter((settlement) => !isArrondiPaymentMethod(settlement.method))
        .sort((a, b) => time(a.paidAt) - time(b.paidAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const cutoff = signedAt == null ? null : time(signedAt);
    const isLater = (s) => s.recordedAfterIssue != null ? s.recordedAfterIssue : cutoff !== null && time(s.paidAt) > cutoff;
    const atSigning = ordered.filter((s) => !isLater(s));
    const later = ordered.filter(isLater);
    return {
        atSigning,
        ...settlementDeclaration(atSigning, exactTotals, currency, direction),
        later,
    };
}
/**
 * The settlement of an invoice as a pure function of its persisted rows.
 *
 * `net` is the certified TTC + PSVB, `credited` the net of its signed credit
 * notes as a positive amount, `payments` every InvoicePayment row, the
 * rounding rows included. The rounding the balance calls for is:
 * - `balanceRaw` when it is non-zero and the money received equals the
 *   payable amount of what is due (net − credited): in franc CFA a balance
 *   under 0,50, or −0,50 when a due ending in ,50 was paid at the franc
 *   above;
 * - in a refund position (more received than the payable amount of what is
 *   due, which only a credit note brings about), the difference between
 *   `balanceRaw` and the refund in the payable unit, so the client is owed
 *   whole units: −11,50 owed is refunded 12 F, a rounding of +0,50;
 * - never in a currency that pays to the cent.
 * The invoice is PAID when nothing is owed (exactly, through the rounding,
 * or in a refund position), PARTIAL when money was received and something
 * is owed.
 */
export function invoiceSettlementState(input) {
    let received = 0;
    let arrondi = 0;
    for (const payment of input.payments) {
        if (isArrondiPaymentMethod(payment.method))
            arrondi += toCentimes(payment.amount);
        else if (isReceivedPaymentMethod(payment.method))
            received += toCentimes(payment.amount);
    }
    const due = toCentimes(input.net) - toCentimes(input.credited ?? 0);
    const balanceRaw = due - received;
    const payableDue = toCentimes(toPayableAmount(centimesToAmount(due), input.currency));
    let arrondiTarget = null;
    if (balanceRaw !== 0 && payableDue === received) {
        arrondiTarget = balanceRaw;
    }
    else if (received > payableDue && balanceRaw < 0) {
        const refund = toCentimes(toPayableAmount(centimesToAmount(-balanceRaw), input.currency));
        arrondiTarget = balanceRaw + refund === 0 ? null : balanceRaw + refund;
    }
    const balance = balanceRaw - arrondi;
    const status = balance <= 0 ? "PAID" : received > 0 ? "PARTIAL" : "PENDING";
    const remaining = toPayableMoney(balance > 0 ? centimesToAmount(balance) : 0, input.currency);
    return {
        received: received,
        balanceRaw: balanceRaw,
        arrondiTarget: arrondiTarget,
        arrondi: arrondi,
        balance: balance,
        status,
        remaining,
    };
}
//# sourceMappingURL=payable-amount.js.map